// react-slickのためのリソース読み込み
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { ApolloProvider } from '@apollo/client';
import { CacheProvider } from '@emotion/react';
import { isBrowser } from '@findy-code/utils/browser';
import { digestMessage } from '@findy-code/utils/crypto';
import {
  graphqlClient,
  useFetchCurrentUserCheckLoginQuery,
} from '@findy-code/yuffie/graphql';
import { localStorageService } from '@findy-code/yuffie/services/local-storage';
import {
  ClarityScript,
  setClarityIdentify,
} from '@findy-code/yuffie/shared/clarity';
import {
  AppActionTypes,
  configureStore,
  UserActionTypes,
} from '@findy-code/yuffie/store';
import { GlobalStyle } from '@findy-code/yuffie/ui/styles/global-style';
import { FindyToast } from '@findy-code/yuffie/ui/toast';
import { insertIntoDataLayer } from '@findy-code/yuffie/utils/data-layer';
import {
  gaSendEvent,
  setUserIdToGaSendEventObj,
} from '@findy-code/yuffie/utils/events/event';
import type { UserRouteHistory } from '@findy-code/yuffie/utils/history/route-history';
import { setUser } from '@sentry/browser';
import { ErrorBoundary } from '@sentry/nextjs';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import type { ComponentType, ReactElement } from 'react';
import { cloneElement, useEffect, useMemo } from 'react';
import { Cookies } from 'react-cookie';
import { setAppElement } from 'react-modal';
import { Provider, useDispatch } from 'react-redux';

import DatadogInit from './datadog-init';
import { EmotionCacheHandle } from './emotion-cache-handle';

// Store設定
const store = configureStore({});

// GTMのdataLayer設定
if (isBrowser()) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    eventCategory: '',
    eventAction: '',
    eventLabel: '',
    eventValue: 0,
    eventNonInteraction: false,
  });
}

// モーダル設定
setAppElement('body');

/**
 *
 * @param {import('next/app').NextWebVitalsMetric} metric
 */
export function reportWebVitals(metric: {
  label?: string;
  name: string;
  value: number;
  id: string;
}) {
  /**
   * Object is based on official Next.js documentation
   *
   * @see {@link https://nextjs.org/docs/advanced-features/measuring-performance#sending-results-to-analytics}
   */
  gaSendEvent({
    event: 'report-web-vitals',
    category:
      metric.label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
    action: metric.name,
    value: Math.round(
      metric.name === 'CLS' ? metric.value * 1000 : metric.value
    ), // values must be integers
    label: metric.id, // id unique to current page load
    nonInteraction: true, // avoids affecting bounce rate.
  });
}

export const App = ({
  Component,
  pageProps,
}: {
  Component: ComponentType;
  pageProps: Record<string, unknown>;
}) => {
  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta content="width=device-width, initial-scale=1.0" name="viewport" />
        <title>
          IT/Webエンジニアの転職・求人サイトFindy – GitHubからスキル偏差値を算出
        </title>
        <meta
          content="ハイクラスエンジニア向け転職・求人サイト。自社開発のWeb企業の求人多数！GitHub登録をすると、IT/Web企業とマッチングします。ユーザーサクセス (キャリア)面談も実施。"
          name="description"
        />
        <meta
          content="IT/Webエンジニアの転職・求人サイトFindy – GitHubからスキル偏差値を算出"
          key="og:title"
          property="og:title"
        />
        <meta content="website" key="og:type" property="og:type" />
        <meta
          content={process.env.NEXT_PUBLIC_FINDY_CODE_URL_BASE}
          key="og:url"
          property="og:url"
        />
        <meta
          content={`${process.env.NEXT_PUBLIC_FINDY_CODE_URL_BASE}/images/ogp.jpg`}
          key="og:image"
          property="og:image"
        />
        <meta
          content="ハイクラスエンジニア向け転職・求人サイト。自社開発のWeb企業の求人多数！GitHub登録をすると、IT/Web企業とマッチングします。ユーザーサクセス (キャリア)面談も実施。"
          key="og:description"
          property="og:description"
        />
        <meta
          content="IT/Webエンジニアの転職・求人サイトFindy – GitHubからスキル偏差値を算出"
          key="og:site_name"
          property="og:site_name"
        />
        <meta content="402379210146302" property="fb:app_id" />
        <meta
          content="ksuyt6djn5elxydsgy8xv14z5wm5sz"
          name="facebook-domain-verification"
        />
        <meta content="summary_large_image" name="twitter:card" />
        <meta content="@findy_code" name="twitter:site" />
        <meta
          content={process.env.NEXT_PUBLIC_FINDY_CODE_URL_BASE}
          name="twitter:domain"
        />
        {process.env.NEXT_PUBLIC_YUFFIE_ENV !== 'production' && (
          <meta content="noindex" name="robots" />
        )}
      </Head>
      <DatadogInit />
      <ErrorBoundary>
        <ApolloProvider client={graphqlClient}>
          <CacheProvider value={EmotionCacheHandle.get()}>
            <GlobalStyle />
            <Provider store={store}>
              <FindyToast />
              <Inner>
                <Component {...pageProps} />
              </Inner>
            </Provider>
          </CacheProvider>
        </ApolloProvider>

        {typeof process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID === 'string' && (
          <Script
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID}');`,
            }}
            id="gtm"
            strategy="afterInteractive"
          />
        )}

        {process.env.NEXT_PUBLIC_FEATURE_CLARITY_USER_ID === 'true' &&
          typeof process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID === 'string' && (
            <ClarityScript />
          )}

        {process.env.NEXT_PUBLIC_YUFFIE_ENV === 'production' && (
          <>
            <Script
              dangerouslySetInnerHTML={{
                __html: `
                  if (!window.afblpcvLpConf) {
                    window.afblpcvLpConf = [];
                  }
                  window.afblpcvLpConf.push({
                    siteId: "a7aa1560"
                  });
                  window.afblpcvLinkConf = {
                    siteId: "a7aa1560",
                    mode: "all"
                  };
                `,
              }}
              id="afblpcv-lp-conf"
              strategy="afterInteractive"
            />
            <Script
              async
              id="afblpcv-lpcv-js"
              src="https://t.afi-b.com/jslib/lpcv.js?cid=a7aa1560&pid=Y16038P"
              strategy="afterInteractive"
            />
            <Script
              dangerouslySetInnerHTML={{
                __html: `
                  (function(){
                  var uqid = "6f0d4d84dec27e8e";
                  var gid = "2268";
                  var a=document.createElement("script");
                  a.dataset.uqid=uqid;a.dataset.gid=gid;a.id="afadfpc-6f0d4d84dec27e8egid2268-"+Date.now();
                  a.src="//ad.presco.asia/fpc/cookie_js.php?scriptId="+encodeURIComponent(a.id);
                  document.head.appendChild(a);
                  })();
                `,
              }}
              id="afadfpc-script"
              strategy="afterInteractive"
            />
          </>
        )}
      </ErrorBoundary>
    </>
  );
};

// `?fr=...`で指定した値をcookieに設定
export function useSetFindyReferrerCookie() {
  const router = useRouter();
  const { isReady } = router;

  useEffect(() => {
    if (isReady) {
      const fr = router.query['fr'];
      if (typeof fr === 'string') {
        new Cookies().set('findy_referrer', fr, { path: '/' });
      }
    }
  }, [isReady, router]);
}

// `?ft=...`で指定した値をcookieに設定
// frと異なり、新規登録以外のトラッキングで使用する
export function useSetFindyTrackingCookie() {
  const router = useRouter();
  const { isReady } = router;

  useEffect(() => {
    if (isReady) {
      const ft = router.query['ft'];
      if (typeof ft === 'string') {
        new Cookies().set('findy_tracking', ft, { path: '/' });
      }
    }
  }, [isReady, router]);
}

export function useSetClickIdToLocalStorage() {
  const router = useRouter();
  const { isReady, query } = router;

  useEffect(() => {
    if (isReady) {
      if (typeof query['gclid'] === 'string') {
        localStorageService.setItem('gclid', query['gclid']);
      }
    }
  }, [isReady, query]);
}

export function useSetRouteHistory() {
  const router = useRouter();
  const { isReady, asPath, pathname, query } = router;

  useEffect(() => {
    if (isReady) {
      let routeHistory: UserRouteHistory = [];
      const currentRoute = { asPath, pathname, query } as const;

      try {
        routeHistory = JSON.parse(
          localStorageService.getItem('routeHistory') || '[]'
        );
      } catch {
        localStorageService.removeItem('routeHistory');
      }

      try {
        if (
          JSON.stringify(currentRoute) !==
          JSON.stringify(routeHistory.slice(-1)[0])
        ) {
          routeHistory.push(currentRoute);

          if (routeHistory.length > 5) {
            routeHistory.shift();
          }

          localStorageService.setItem(
            'routeHistory',
            JSON.stringify(routeHistory)
          );
        }
      } catch {
        //getItemのトライキャッチによりここに来ることはないはずだが念の為
        localStorageService.removeItem('routeHistory');
      }
    }
  }, [isReady, asPath, pathname, query]);
}

export const pathnameToLabelSuffixMap = {
  '/': 'トップページ',
  '/lp': 'LP',
} as const;
export function useSendGclid(
  userId: number | undefined,
  isLoadingUserData: boolean
) {
  const router = useRouter();
  const { isReady, query, pathname } = router;

  useEffect(() => {
    if (isReady && !isLoadingUserData) {
      const gclid = query['gclid'];
      if (typeof gclid === 'string') {
        if (pathname === '/' || pathname === '/lp') {
          gaSendEvent({
            category: 'google_ad',
            action: 'view_via_gcl',
            label: `Google広告計測_${pathnameToLabelSuffixMap[pathname]}`,
            gclid,
            userId,
          });
        }
      }
    }
  }, [isReady, query, pathname, userId, isLoadingUserData]);
}

export function useSendPageViewEvent(userId: number | undefined) {
  const router = useRouter();

  useEffect(() => {
    if (router.isReady && userId) {
      gaSendEvent({
        category: 'page_view',
        action: 'page_view',
        userId,
        nonInteraction: true,
      });
    }
  }, [router.isReady, router.asPath, userId]);
}

export function useSendClarityPageView(userId: number | undefined) {
  const router = useRouter();

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_FEATURE_CLARITY_USER_ID !== 'true') {
      return;
    }

    if (router.isReady && userId) {
      setClarityIdentify(userId);
    }
  }, [router.isReady, router.asPath, userId]);
}

export function useSendCriteoPageView(userEmail: string | undefined | null) {
  const router = useRouter();

  useEffect(() => {
    (async () => {
      const jobPathRegex = /^\/companies\/[^/]+\/jobs\/[^/]+$/;
      const companyPathRegex = /^\/companies\/[^/]+$/;
      const currentPath = router.pathname;

      if (
        router.isReady &&
        userEmail &&
        (jobPathRegex.test(currentPath) ||
          companyPathRegex.test(currentPath) ||
          currentPath.startsWith('/recommends') ||
          currentPath === '/')
      ) {
        insertIntoDataLayer({
          event: 'Criteo_PageView',
          email: await digestMessage({ message: userEmail }),
        });
      }
    })();
  }, [router.isReady, router.pathname, userEmail]);
}

type InnerChildProps = {
  isLogin?: boolean;
  isLoadingUserData?: boolean;
  userId?: number;
  careerMotivation?: string | number;
  userShareHash?: string;
};

export function Inner({
  children,
}: {
  children: ReactElement<InnerChildProps>;
}) {
  useSetFindyReferrerCookie();
  useSetFindyTrackingCookie();
  useSetClickIdToLocalStorage();
  useSetRouteHistory();

  const dispatch = useDispatch();

  const { data: currentUserData, loading: isLoadingUserData } =
    useFetchCurrentUserCheckLoginQuery({
      fetchPolicy: 'network-only',
      errorPolicy: 'none',
      onError() {
        dispatch({
          type: AppActionTypes.LOADED,
        });
      },
      onCompleted(data) {
        dispatch({
          type: AppActionTypes.LOADED,
        });

        const user = data?.currentUser;

        if (user && typeof user?.id !== 'undefined') {
          setUserIdToGaSendEventObj(user.id);

          setUser({ id: user.id.toString() });

          dispatch({
            type: UserActionTypes.FETCH_USER_INFO,
            payload: {
              user_info: {
                id: user.id,
                email: user.email,
                name: user.profile?.name || '',
                notifications: {
                  has_input_required_profile:
                    user.setting?.hasInputRequiredProfile || false,
                },
                profile: {
                  name: user.profile?.name || '',
                  company_name: user.profile?.companyName || '',
                },
                // redux管理のprogressを利用しているのは、おそらくsharedの<Header />だけ
                progress: user.profile?.inputProgress || 0,
                share_info: {
                  share_hash: user.shareInfo.shareHash,
                },
              },
              unread_count: user.unreadCount,
              unread_all_message_context_count:
                user.unreadAllMessageContextCount,
              career_motivation: {
                user_answer_id: user.profile?.careerMotivation,
              },
            },
          });
        }
      },
    });

  const user = currentUserData?.currentUser;
  const isLogin = !!user?.id;
  const userId = user?.id;
  const careerMotivation = user?.profile?.careerMotivation;
  const userShareHash = user?.shareInfo.shareHash;

  useSendPageViewEvent(user?.id);
  useSendCriteoPageView(user?.email);
  useSendClarityPageView(user?.id);
  useSendGclid(user?.id, isLoadingUserData);

  const content = useMemo(
    () =>
      cloneElement(children, {
        isLogin,
        isLoadingUserData,
        userId,
        careerMotivation,
        userShareHash,
      }),
    [
      children,
      isLogin,
      isLoadingUserData,
      userId,
      careerMotivation,
      userShareHash,
    ]
  );

  return content;
}

export default App;
